import env from '@config/env';
import { ROUTE_PATHS, isAddNewBusinessNavigation } from '@config/routes';
import SearchBusinessPageSkeleton from '@loaders/SearchBusinessPageSkeleton';
import OnboardingPageLayout from '@modules/merchant/authentication/components/OnboardingPageLayout';
import BusinessMapPreview from '@modules/merchant/onboarding/components/BusinessMapPreview';
import BusinessSearchField from '@modules/merchant/onboarding/components/BusinessSearchField';
import BusinessSearchResults from '@modules/merchant/onboarding/components/BusinessSearchResults';
import OnboardingBackLink from '@modules/merchant/onboarding/components/OnboardingBackLink';
import {
  fetchAppConfig,
  selectAppConfigHydrated,
  selectNeedsBusinessSearch,
} from '@redux/merchant/merchantSlice';
import { setBusinessSearchQuery, setSelectedBusiness } from '@redux/onboarding/onboardingSlice';
import { merchantAuthApi } from '@services/api';
import {
  extractPlacePredictions,
  getPlaceAutocompleteStatus,
  mapPlacePrediction,
} from '@services/placesApi';
import { getApiErrorMessage } from '@utils/apiError';
import { debounce } from '@utils/debounce';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router';

const SearchBusinessPage = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAddingNewBusiness = isAddNewBusinessNavigation(location.state);
  const isNewUser = useSelector((state) => Boolean(state.onboarding.isNewUser));
  const searchQuery = useSelector((state) => state.onboarding.businessSearch.query);
  const needsBusinessSearch = useSelector(selectNeedsBusinessSearch);
  const appConfigHydrated = useSelector(selectAppConfigHydrated);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  useEffect(() => {
    document.title = `Search Business | ${env.APP_NAME}`;
  }, []);

  // Onboarding routes sit outside MerchantLayout / header, so config must load here.
  useEffect(() => {
    if (appConfigHydrated) return;
    dispatch(fetchAppConfig());
  }, [appConfigHydrated, dispatch]);

  const fetchPlaces = useCallback(async (query) => {
    const input = query.trim();
    if (!input) {
      setResults([]);
      setSearchError('');
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchError('');

    try {
      const response = await merchantAuthApi.placeAutocomplete({ input });
      const predictions = extractPlacePredictions(response);
      const status = getPlaceAutocompleteStatus(response);

      if (response?.data?.success === false) {
        setResults([]);
        setSearchError(
          response?.data?.data?.errorMessage ||
            response?.data?.message ||
            'Failed to search businesses',
        );
        return;
      }

      if (!status || status === 'OK' || status === 'ZERO_RESULTS') {
        setResults(predictions.map(mapPlacePrediction).filter((item) => item.placeId));
        return;
      }

      setResults([]);
      setSearchError(
        response?.data?.error_message ||
          response?.data?.message ||
          `Places search failed (${status}).`,
      );
    } catch (error) {
      setResults([]);
      setSearchError(getApiErrorMessage(error, 'Failed to search businesses'));
    } finally {
      setIsSearching(false);
    }
  }, []);

  const debouncedFetchPlaces = useMemo(() => debounce(fetchPlaces, 400), [fetchPlaces]);

  useEffect(() => {
    debouncedFetchPlaces(searchQuery);
  }, [searchQuery, debouncedFetchPlaces]);

  const showResults = searchQuery.trim().length > 0;

  const handleSearchChange = (event) => {
    dispatch(setBusinessSearchQuery(event.target.value));
  };

  const handleSelectBusiness = useCallback(
    (business) => {
      const placeId = business.placeId || business.id;
      if (!placeId) return;

      dispatch(setBusinessSearchQuery(''));
      setResults([]);
      setSearchError('');
      dispatch(setSelectedBusiness(business));
      navigate(ROUTE_PATHS.BUSINESS_DETAIL, { state: location.state });
    },
    [dispatch, location.state, navigate],
  );

  if (!appConfigHydrated) {
    return <SearchBusinessPageSkeleton />;
  }

  if (!needsBusinessSearch && !isAddingNewBusiness) {
    return <Navigate to={ROUTE_PATHS.SETUP_GUIDE} replace />;
  }

  return (
    <OnboardingPageLayout>
      <div className="flex w-full flex-col gap-form">
        {(!isNewUser || isAddingNewBusiness) && (
          <OnboardingBackLink
            to={isAddingNewBusiness ? ROUTE_PATHS.SETUP_GUIDE : ROUTE_PATHS.CREATE_PROFILE}
          />
        )}

        <div className="flex w-full flex-col gap-0.75 desktop:gap-1">
          <h1 className="desktop:text-[32px] text-[24px] font-semibold leading-tight text-merchant-text-primary">
            Search your business
          </h1>
          <p className="max-w-md desktop:text-[16px] text-sm font-medium leading-normal text-merchant-text-secondary">
            Search and select your business from Google.
          </p>
        </div>

        <div className="flex w-full flex-col gap-form">
          <BusinessSearchField value={searchQuery} onChange={handleSearchChange} />
          {showResults ? (
            <BusinessSearchResults
              results={results}
              loading={isSearching}
              error={searchError}
              onSelect={handleSelectBusiness}
            />
          ) : (
            <BusinessMapPreview />
          )}
        </div>
      </div>
    </OnboardingPageLayout>
  );
});

SearchBusinessPage.displayName = 'SearchBusinessPage';

export default SearchBusinessPage;
